import React,{useEffect,useState} from 'react';
import supabase from '../supabase/supabase';
import QuoteTable from '../components/QuoteTable';
import {
  PAGE_SIZE,
  archiveQuote,
  loadAdminQuoteCounts,
  loadAdminQuotes,
  restoreQuote
} from '../services/adminQuoteService';

function QuoteList({onNavigate}) {
  const [view,setView]=useState('active');
  const [searchInput,setSearchInput]=useState('');
  const [search,setSearch]=useState('');
  const [page,setPage]=useState(1);
  const [quotes,setQuotes]=useState([]);
  const [totalCount,setTotalCount]=useState(0);
  const [activeCount,setActiveCount]=useState(null);
  const [archivedCount,setArchivedCount]=useState(null);
  const [loading,setLoading]=useState(true);
  const [countsLoading,setCountsLoading]=useState(true);
  const [error,setError]=useState('');
  const [message,setMessage]=useState('');

  const refreshCounts=async ()=> {
    const counts=await loadAdminQuoteCounts();
    setActiveCount(counts.activeCount);
    setArchivedCount(counts.archivedCount);
  };

  useEffect(()=> {
    const timer=window.setTimeout(()=> {
      setPage(1);
      setSearch(searchInput.trim());
    },300);

    return ()=> window.clearTimeout(timer);
  },[searchInput]);

  useEffect(()=> {
    let active=true;

    const load=async ()=> {
      setLoading(true);
      setError('');

      try {
        const result=await loadAdminQuotes({
          archived: view==='archived',
          search,
          page
        });

        if (!active) {
          return;
        }

        setQuotes(result.quotes);
        setTotalCount(result.count);
      } catch (loadError) {
        if (!active) {
          return;
        }

        console.error('Admin quote list query failed:',loadError);
        setError('Quotations could not be loaded. Please try again.');
        setQuotes([]);
        setTotalCount(0);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return ()=> {
      active=false;
    };
  },[view,search,page]);

  useEffect(()=> {
    let active=true;

    const loadCounts=async ()=> {
      setCountsLoading(true);

      try {
        const counts=await loadAdminQuoteCounts();

        if (!active) {
          return;
        }

        setActiveCount(counts.activeCount);
        setArchivedCount(counts.archivedCount);
      } catch (countError) {
        if (!active) {
          return;
        }

        console.error('Admin quote counts query failed:',countError);
      } finally {
        if (active) {
          setCountsLoading(false);
        }
      }
    };

    loadCounts();

    return ()=> {
      active=false;
    };
  },[]);

  const refreshCurrentPage=async ()=> {
    const [result]=await Promise.all([
      loadAdminQuotes({
        archived: view==='archived',
        search,
        page
      }),
      refreshCounts()
    ]);

    setQuotes(result.quotes);
    setTotalCount(result.count);
  };

  const changeView=(nextView)=> {
    setView(nextView);
    setPage(1);
    setMessage('');
  };

  const clearSearch=()=> {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleArchive=async (quote)=> {
    await archiveQuote(quote.id);
    setMessage('Quote archived');
    await refreshCurrentPage();
  };

  const handleRestore=async (quote)=> {
    await restoreQuote(quote.id);
    setMessage('Quote restored');
    await refreshCurrentPage();
  };

  const handleDelete=async (quote)=> {
    const {data,error: deleteError}=await supabase
      .from('quotes')
      .delete()
      .eq('id',quote.id)
      .select('id')
      .maybeSingle();

    if (deleteError) {
      console.error('Supabase quote deletion error:',deleteError);
      throw new Error(deleteError.message || 'Unable to delete quotation.');
    }

    if (!data?.id) {
      const missingDeleteError=new Error(
        'Unable to delete quotation. No quote was deleted.'
      );
      console.error('Supabase quote deletion error:',missingDeleteError);
      throw missingDeleteError;
    }

    setMessage('Quote deleted');
    await refreshCurrentPage();
  };

  const totalPages=Math.max(1,Math.ceil(totalCount / PAGE_SIZE));

  useEffect(()=> {
    if (page>totalPages) {
      setPage(totalPages);
    }
  },[page,totalPages]);

  const activeLabel=countsLoading || activeCount===null
    ? 'Active'
    : `Active (${activeCount})`;

  const archivedLabel=countsLoading || archivedCount===null
    ? 'Archived'
    : `Archived (${archivedCount})`;

  return (
    <main className="admin-page">
      <div className="admin-kicker">Quotations</div>

      <div className="page-title-row">
        <div>
          <h1>Production Quotes</h1>
          <p>Prepare and manage client quotations.</p>
        </div>

        <button
          className="button button--dark"
          onClick={()=> onNavigate('builder')}
          type="button"
        >
          + New Quote
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '18px',
          flexWrap: 'wrap'
        }}
      >
        <div style={{display: 'flex',gap: '6px'}}>
          <button
            className={`button ${view==='active' ? 'button--dark' : 'button--outline'}`}
            onClick={()=> changeView('active')}
            type="button"
          >
            {activeLabel}
          </button>

          <button
            className={`button ${view==='archived' ? 'button--dark' : 'button--outline'}`}
            onClick={()=> changeView('archived')}
            type="button"
          >
            {archivedLabel}
          </button>
        </div>

        <div style={{display: 'flex',alignItems: 'center',gap: '8px',minWidth: 'min(100%,360px)'}}>
          <input
            aria-label="Search quotations"
            onChange={(event)=> setSearchInput(event.target.value)}
            placeholder="Search reference,client,title,or email"
            style={{
              minHeight: '43px',
              flex: 1,
              padding: '10px 12px',
              border: '1px solid #cbc8c0',
              borderRadius: '2px',
              background: 'var(--paper)',
              fontSize: '13px'
            }}
            type="search"
            value={searchInput}
          />

          {searchInput && (
            <button
              className="text-button"
              onClick={clearSearch}
              type="button"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {message && (
        <p
          role="status"
          style={{marginBottom: '14px',color: 'var(--green)',fontSize: '12px'}}
        >
          {message}
        </p>
      )}

      {error && (
        <p
          role="alert"
          style={{marginBottom: '14px',color: 'var(--accent-dark)',fontSize: '12px'}}
        >
          {error}
        </p>
      )}

      {loading ? (
        <p style={{padding: '24px 0',color: 'var(--muted)',fontSize: '13px'}}>
          Loading quotations…
        </p>
      ) : (
        <>
          <QuoteTable
            onArchive={handleArchive}
            onDelete={handleDelete}
            onEdit={(quoteId)=> onNavigate('builder',quoteId)}
            onRestore={handleRestore}
            quotes={quotes}
            view={view}
          />

          {totalCount>0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '14px',
                marginTop: '18px'
              }}
            >
              <button
                className="button button--outline"
                disabled={page<=1}
                onClick={()=> setPage((current)=> Math.max(1,current - 1))}
                type="button"
              >
                Previous
              </button>

              <span style={{color: 'var(--muted)',fontSize: '12px'}}>
                Page {page} of {totalPages}
              </span>

              <button
                className="button button--outline"
                disabled={page>=totalPages}
                onClick={()=> setPage((current)=> Math.min(totalPages,current + 1))}
                type="button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}

export default QuoteList;